"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const Icon_1 = require("../../foundations/Icon/Icon");
const Text_1 = require("../../foundations/Typography/Text");
const useWarning_1 = require("../../hooks/useWarning");
const Button_1 = require("../Button");
const Overlay_1 = require("../Overlay");
const useFocusTrap = (isActive, modalRef) => {
    react_1.default.useEffect(() => {
        if (!isActive || !modalRef.current) {
            return;
        }
        const modalSpace = modalRef.current;
        const focusableSelectors = 'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
        const focusableElements = Array.from(modalSpace.querySelectorAll(focusableSelectors));
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const handleKeyDown = (event) => {
            if (event.key === "Tab") {
                if (event.shiftKey) {
                    if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement === null || lastElement === void 0 ? void 0 : lastElement.focus();
                    }
                }
                else {
                    if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus();
                    }
                }
            }
        };
        modalSpace.addEventListener("keydown", handleKeyDown);
        firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus(); // first focusable element is focused on mount
        return () => {
            modalSpace === null || modalSpace === void 0 ? void 0 : modalSpace.removeEventListener("keydown", handleKeyDown);
        };
    }, [isActive, modalRef]);
};
const Modal = ({ children, title, isOpen, onDismiss, size = "md", buttons }) => {
    const { warn } = (0, useWarning_1.useWarning)();
    const modalRef = react_1.default.useRef(null);
    const clickingContent = react_1.default.useRef(false);
    const contentRef = react_1.default.useRef(null);
    const [showDivider, setShowDivider] = react_1.default.useState(false);
    const dismissible = !!onDismiss;
    const transform = isOpen
        ? ["translateY(0px)", "unset", null]
        : ["translateY(100%)", "unset", null];
    const { modalSize, modalPush } = react_1.default.useMemo(() => {
        const modalDimensions = {
            modalSize: [4, 6, 8],
            modalPush: [0, 1, 2]
        };
        switch (size) {
            case "xs":
                modalDimensions.modalSize = [4, 4, 4];
                modalDimensions.modalPush = [0, 2, 4];
                break;
            case "sm":
                modalDimensions.modalSize = [4, 4, 6];
                modalDimensions.modalPush = [0, 2, 3];
                break;
            case "lg":
                modalDimensions.modalSize = [4, 8, 10];
                modalDimensions.modalPush = [0, 0, 1];
                break;
        }
        return modalDimensions;
    }, [size]);
    const handleClickOutside = react_1.default.useCallback(() => {
        if (!clickingContent.current) {
            if (onDismiss && dismissible) {
                onDismiss();
            }
        }
        clickingContent.current = false;
    }, [dismissible, onDismiss]);
    const dismissControl = react_1.default.useMemo(() => ({
        color: "control-enabled",
        ":hover": { color: "control-hover" },
        ":active": { color: "control-click" },
        ":focus-visible": {
            outline: "2px solid",
            outlineColor: "focus",
            outlineOffset: "2px",
            borderRadius: "radius-round"
        }
    }), []);
    useFocusTrap(isOpen, modalRef);
    if (buttons && buttons.length > 2) {
        warn("Modal: Adding more than two buttons may impact layout on smaller screens.");
    }
    // calculate children height and show divider if scrollable content exists
    react_1.default.useEffect(() => {
        const updateDividerVisibility = () => {
            const content = contentRef.current;
            if (content) {
                const { scrollTop, scrollHeight, clientHeight } = content;
                setShowDivider(scrollHeight > clientHeight &&
                    (scrollTop > 0 || scrollTop + clientHeight < scrollHeight));
            }
        };
        updateDividerVisibility(); // initialize visibility on mount or modal open
        const content = contentRef.current;
        if (content) {
            content.addEventListener("scroll", updateDividerVisibility);
            return () => content.removeEventListener("scroll", updateDividerVisibility);
        }
    }, [isOpen]); // recalculate on modal open
    return (react_1.default.createElement(Overlay_1.Overlay, { visible: isOpen, onDismiss: onDismiss, sx: {
            alignItems: "center",
            display: "flex"
        } },
        react_1.default.createElement(foundations_1.Grid, { initial: true, onClick: handleClickOutside },
            react_1.default.createElement(foundations_1.GridColumn, { size: modalSize, fillGap: true, sx: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: ["flex-end", "center", null],
                    minHeight: ["100dvh", "100vh"],
                    transition: "transition-ease-out-medium",
                    transform: transform
                }, push: modalPush },
                react_1.default.createElement(foundations_1.Box, { ref: modalRef, role: "dialog", "aria-modal": "true", "aria-labelledby": title ? "modal-title" : undefined, onClick: () => {
                        clickingContent.current = true;
                    }, sx: {
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "surface-neutral-default",
                        cursor: "initial",
                        maxHeight: (theme) => `calc(100vh - ${theme.sizes[28]}px)`,
                        overflow: "auto",
                        borderRadius: [null, "radius-2"],
                        border: "1px solid",
                        borderColor: "stroke-neutral-default",
                        boxShadow: [null, "shadow-100"],
                        py: "spacing-7",
                        gap: "spacing-5"
                    } },
                    (title || dismissible) && (react_1.default.createElement(foundations_1.Box, { sx: {
                            display: "flex",
                            justifyContent: title ? "space-between" : "flex-end",
                            alignItems: "center",
                            flexShrink: 0,
                            px: "spacing-7"
                        } },
                        title && (react_1.default.createElement(Text_1.Text, { variant: "title-1-medium", id: "modal-title" }, title)),
                        dismissible && (react_1.default.createElement(foundations_1.Target, { onClick: () => {
                                clickingContent.current = true;
                                onDismiss();
                            }, "aria-label": "Close Modal", sx: Object.assign(Object.assign({}, dismissControl), { height: "spacing-7", width: "spacing-7" }) },
                            react_1.default.createElement(Icon_1.Icon, { icon: "x", size: "sm", color: "currentColor" }))))),
                    react_1.default.createElement(foundations_1.Box, { ref: contentRef, sx: {
                            flexGrow: 1,
                            overflowY: "auto",
                            px: "spacing-7",
                            // using border + padding to visually differentiate the scroll area
                            py: showDivider ? "spacing-5" : "spacing-none",
                            borderBottom: showDivider ? "1px solid" : "none",
                            borderTop: showDivider ? "1px solid" : "none",
                            borderColor: "stroke-neutral-default"
                        } }, children),
                    buttons && (react_1.default.createElement(foundations_1.Box, { sx: {
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-start",
                            alignSelf: "stretch",
                            gap: "spacing-3",
                            px: "spacing-7"
                        } }, buttons.map((button, idx) => {
                        return (react_1.default.createElement(Button_1.Button, Object.assign({ key: `modal-button-${idx}`, variant: idx < buttons.length - 1 ? "outline" : "filled" }, button), button.label));
                    }))))))));
};
exports.Modal = Modal;
//# sourceMappingURL=Modal.js.map